import { ContentFieldType } from "../../types/cms/contentField.type";
import { buildChannelPrefix } from "../../utils/canonicalUrl";
import { clamp } from "../../utils/clamp";
import { buildEntryPath } from "../../utils/entryUrl";
import { resolveEntryTitle } from "../../utils/entryTitle";
import { interpolate, type DynamicStructure } from "../../utils/interpolate";
import { MegaMenuStatus, resolveMegaMenuSource } from "../../utils/megaMenuSource";
import { BAR_LAYOUT_REM, BLUR_PX, COLOR_KEYS, FORMAT_SLOTS, MEGA_MENU_COLUMNS, MENU_FIT_REFERENCE_VIEWPORT_PX } from "./constants";
import {
  BlurStrength,
  HeaderScrollState,
  HeaderV2Layout,
  LogoRedirectType,
  MegaMenuHeight,
  MegaMenuWidth,
  MenuItemMode,
  MenuRedirectType,
  OverlapMode,
  SectionPosition,
  ButtonMobileDisplay,
  type HeaderStateColors,
  type HeaderV2Button,
  type HeaderV2Colors,
  type HeaderV2Link,
  type LogoRedirectConfig,
  type MegaMenuCard,
  type MegaMenuConfig,
  type MegaMenuData,
  type MegaMenuEntry,
  type MegaMenuItemConfig,
  type SectionPlacement,
} from "./types";

export const resolveSlots = ({ layout }: { layout?: HeaderV2Layout }) =>
  FORMAT_SLOTS[layout && layout in FORMAT_SLOTS ? layout : HeaderV2Layout.BASIC];

export const resolveSectionPosition = ({
  fixed,
  overlapFirstBlock,
  isBuilder,
}: {
  fixed?: boolean;
  overlapFirstBlock?: boolean;
  isBuilder?: boolean;
}): SectionPlacement => {
  const inFlow = fixed ? SectionPosition.STICKY : SectionPosition.RELATIVE;
  if (!overlapFirstBlock) return { position: inFlow, overlap: OverlapMode.NONE };
  if (isBuilder) return { position: inFlow, overlap: OverlapMode.MARGIN };
  return fixed
    ? { position: SectionPosition.STICKY, overlap: OverlapMode.COLLAPSE }
    : { position: SectionPosition.ABSOLUTE, overlap: OverlapMode.NONE };
};

export const resolveStateColors = ({
  colors,
  state,
}: {
  colors?: HeaderV2Colors;
  state: HeaderScrollState;
}): HeaderStateColors => {
  const top = colors?.top ?? {};
  if (state === HeaderScrollState.TOP) return { ...top };

  const scrolled = colors?.scrolled ?? {};
  const merged: HeaderStateColors = {};
  for (const key of COLOR_KEYS) {
    merged[key] = scrolled[key] || top[key] || "";
  }
  return merged;
};

export const colorsToCssVars = ({ colors }: { colors: HeaderStateColors }): Record<string, string> => ({
  "--hv2-background": colors.background || "var(--general-background, #F1F5F9)",
  "--hv2-border": colors.border || "transparent",
  "--hv2-divider": colors.border || "var(--general-border, #E2E8F0)",
  "--hv2-links": colors.links || "var(--text-body, #475569)",
  "--hv2-links-hover": colors.linksHover || colors.links || "var(--text-title, #0F172A)",
  "--hv2-submenu": colors.submenu || "var(--cards-background, #FFFFFF)",
  "--hv2-submenu-text": colors.submenuText || "var(--text-body, #475569)",
  "--hv2-submenu-border": colors.submenuBorder || colors.border || "var(--general-border, #E2E8F0)",
  "--hv2-submenu-desc":
    colors.submenuDescription ||
    `color-mix(in srgb, ${colors.submenuText || "var(--text-body, #475569)"} 80%, transparent)`,
  "--hv2-submenu-icon": colors.submenuIcon || colors.submenuText || "var(--text-body, #475569)",
  "--hv2-icons": colors.icons || colors.links || "var(--text-body, #475569)",
  "--hv2-buttons-text": colors.buttonsText || "var(--buttons-text, #FFFFFF)",
  "--hv2-buttons-background": colors.buttonsBackground || "var(--buttons-background, #3754E2)",
  "--hv2-buttons-border": colors.buttonsBorder || "var(--buttons-border, #3754E2)",
  "--hv2-wa-bg": colors.whatsappBackground || "#25D366",
  "--hv2-wa-icon": colors.whatsappIcon || "#FFFFFF",
  "--hv2-wa-border": colors.whatsappBorder || colors.whatsappBackground || "#25D366",
  "--hv2-phone-bg": colors.phoneBackground || "transparent",
  "--hv2-phone-icon": colors.phoneIcon || colors.icons || colors.links || "var(--text-body, #475569)",
  "--hv2-phone-border": colors.phoneBorder || colors.border || "var(--general-border, #E2E8F0)",
  "--hv2-outline-text": colors.outlineText || colors.links || "var(--text-body, #475569)",
  "--hv2-outline-border": colors.outlineBorder || colors.buttonsBackground || "var(--buttons-background, #3754E2)",
  "--hv2-link-text": colors.linkText || colors.links || "var(--text-body, #475569)",
  "--hv2-units-text": colors.unitsText || colors.links || "var(--text-body, #475569)",
  "--hv2-units-bg": colors.unitsBackground || "transparent",
  "--hv2-units-border": colors.unitsBorder || colors.border || "var(--general-border, #E2E8F0)",
  "--hv2-units-divider": colors.unitsDivider || colors.border || "var(--general-border, #E2E8F0)",
  "--hv2-topbar-bg": colors.topBarBackground || "transparent",
  "--hv2-topbar-border": colors.topBarBorder || colors.border || "var(--general-border, #E2E8F0)",
});

export const megaMenuCssVars = ({ megaMenu }: { megaMenu: MegaMenuConfig }): Record<string, string> => ({
  "--hv2-mm-panel-bg": megaMenu.panelBackground || "var(--cards-background, #FFFFFF)",
  "--hv2-mm-panel-border": megaMenu.panelBorder || "var(--cards-border, #E2E8F0)",
  "--hv2-mm-thumb-bg": megaMenu.thumbBackground || "color-mix(in srgb, var(--hv2-mm-title) 5%, transparent)",
  "--hv2-mm-title": megaMenu.thumbTitle || "var(--cards-title, #0F172A)",
  "--hv2-mm-description": megaMenu.thumbDescription || "var(--cards-body, #475569)",
  "--hv2-mm-tag": megaMenu.thumbTag || megaMenu.thumbDescription || "var(--cards-body, #475569)",
  "--hv2-mm-seeall": megaMenu.seeAllColor || megaMenu.thumbTitle || "var(--cards-title, #0F172A)",
  ...(megaMenu.cardCtaColor ? { "--hv2-mm-cta": megaMenu.cardCtaColor } : {}),
  ...(megaMenu.cardCtaBackground ? { "--hv2-mm-cta-bg": megaMenu.cardCtaBackground } : {}),
});

export const resolveMegaMenuSize = ({
  width,
  height,
}: {
  width?: MegaMenuWidth;
  height?: MegaMenuHeight;
}): { width: MegaMenuWidth; fullHeight: boolean } => ({
  width: width === MegaMenuWidth.CONTENT ? MegaMenuWidth.CONTENT : MegaMenuWidth.FULL,
  fullHeight: height === MegaMenuHeight.FULLSCREEN,
});

export const clampSubmenuColumns = ({ value }: { value?: number }): number =>
  clamp({ value: Math.round(Number(value) || 1), min: 1, max: 3 });

export const submenuIsWide = ({ link }: { link: HeaderV2Link }): boolean =>
  clampSubmenuColumns({ value: link.submenuColumns }) > 1 ||
  !!link.children?.some((child) => child.isActive !== false && (child.description || child.icon));

export const resolveSubmenuPlacement = ({
  item,
  panelWidth,
  bounds,
}: {
  item: { left: number; bottom: number };
  panelWidth: number;
  bounds: { left: number; right: number; bottom: number };
}): { shift: number; drop: number } => {
  const overflow = item.left + panelWidth - bounds.right;
  const room = Math.max(0, item.left - bounds.left);
  return {
    shift: overflow > 0 ? -Math.min(overflow, room) : 0,
    drop: Math.max(0, bounds.bottom - item.bottom),
  };
};

export const buttonShowsIcon = ({ button }: { button: HeaderV2Button }): boolean =>
  button.showIcon !== false && !!button.icon;

export const resolveButtonMinRem = ({ withIcon }: { withIcon: boolean }): number =>
  BAR_LAYOUT_REM.truncatedMinWidth + (withIcon ? BAR_LAYOUT_REM.buttonIcon + BAR_LAYOUT_REM.buttonIconGap : 0);

export const resolveButtonsMinRem = ({ buttons }: { buttons: HeaderV2Button[] }): number =>
  buttons.length > 0
    ? buttons.reduce((sum, button) => sum + resolveButtonMinRem({ withIcon: buttonShowsIcon({ button }) }), 0) +
      (buttons.length - 1) * BAR_LAYOUT_REM.desktopGroupGap
    : 0;

export const resolveRightGroupMinRem = ({
  contacts,
  buttons,
  units,
  divider,
  groups,
}: {
  contacts: number;
  buttons: HeaderV2Button[];
  units: boolean;
  divider: boolean;
  groups: number;
}): number =>
  (contacts > 0 ? contacts * BAR_LAYOUT_REM.contactSize + (contacts - 1) * BAR_LAYOUT_REM.desktopGroupGap : 0) +
  resolveButtonsMinRem({ buttons }) +
  (units ? BAR_LAYOUT_REM.truncatedMinWidth : 0) +
  (divider ? BAR_LAYOUT_REM.divider : 0) +
  Math.max(0, groups - 1) * BAR_LAYOUT_REM.rightGroupGap;

export const resolveMenuFitReferenceWidth = ({ compact, remPx }: { compact: boolean; remPx: number }): number =>
  Math.min(
    compact ? BAR_LAYOUT_REM.compactMaxWidth * remPx : Number.POSITIVE_INFINITY,
    MENU_FIT_REFERENCE_VIEWPORT_PX - 2 * BAR_LAYOUT_REM.barGutter * remPx,
  );

export const fitsMenuAtReference = ({
  fixedWidths,
  menuWidth,
  rightMinWidth,
  gap,
  menuMargin,
  referenceWidth,
}: {
  fixedWidths: number[];
  menuWidth: number;
  rightMinWidth: number | null;
  gap: number;
  menuMargin: number;
  referenceWidth: number;
}): boolean => {
  if (menuWidth <= 0 || referenceWidth <= 0) return true;
  const parts = [...fixedWidths.filter((width) => width > 0), menuWidth, ...(rightMinWidth === null ? [] : [rightMinWidth])];
  const required = parts.reduce((sum, width) => sum + width, 0) + gap * (parts.length - 1) + menuMargin;
  return required <= referenceWidth;
};

export const resolveBlurPx = ({ enabled, strength }: { enabled?: boolean; strength?: BlurStrength }): number =>
  enabled ? BLUR_PX[strength && strength in BLUR_PX ? strength : BlurStrength.MD] : 0;

const getStringValue = ({ value, fallback = "" }: { value: unknown; fallback?: string }): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    for (const key of ["slug", "value", "href", "url", "path"]) {
      if (key in obj && typeof obj[key] === "string") return (obj[key] as string) || fallback;
    }
    const strings = Object.values(obj).filter((v) => typeof v === "string");
    if (strings.length === 1) return strings[0] as string;
  }
  return fallback;
};

export const getLinkUrl = ({ item }: { item: HeaderV2Link | HeaderV2Button }): string => {
  if (item.redirect) {
    switch (item.redirect.type) {
      case MenuRedirectType.PAGE: {
        const slug = getStringValue({ value: item.redirect.pageSlug, fallback: "/" });
        return slug && slug !== "/" && !slug.startsWith("/") ? `/${slug}` : slug;
      }
      case MenuRedirectType.EXTERNAL_URL:
        return getStringValue({ value: item.redirect.url, fallback: "#" });
      case MenuRedirectType.SCROLL_TO_BLOCK: {
        const base = getStringValue({ value: item.redirect.scrollPageSlug });
        const normalizedBase = base && !base.startsWith("/") ? `/${base}` : base;
        const blockId = getStringValue({ value: item.redirect.blockId });
        return blockId ? `${normalizedBase}#${blockId}` : normalizedBase || "#";
      }
      default:
        return "#";
    }
  }
  return getStringValue({ value: item.link, fallback: "#" });
};

export const shouldOpenInNewTab = ({ item }: { item: HeaderV2Link | HeaderV2Button }): boolean => {
  if (item.redirect) {
    return item.redirect.type === MenuRedirectType.EXTERNAL_URL && (item.redirect.openInNewTab || false);
  }
  return (item as HeaderV2Link).newTab || (item as HeaderV2Button).openInNewTab || false;
};

export const getLogoLink = ({
  redirect,
}: {
  redirect?: LogoRedirectConfig;
}): { href: string; target: string } | null => {
  if (!redirect) return { href: "/", target: "_self" };
  if (redirect.enabled === false) return null;
  const type = redirect.type || LogoRedirectType.HOMEPAGE;
  if (type === LogoRedirectType.HOMEPAGE) return { href: "/", target: "_self" };
  if (type === LogoRedirectType.URL && redirect.url) {
    return { href: redirect.url, target: redirect.openInNewTab ? "_blank" : "_self" };
  }
  if (type === LogoRedirectType.PAGE && redirect.pageSlug) {
    const slug = redirect.pageSlug.startsWith("/") ? redirect.pageSlug : `/${redirect.pageSlug}`;
    return { href: slug, target: redirect.openInNewTab ? "_blank" : "_self" };
  }
  return null;
};

export const linkKey = ({ link, index }: { link: HeaderV2Link; index: number }) => String(link.id ?? index);

export const hasActiveChildren = ({ link }: { link: HeaderV2Link }): boolean =>
  link.enableLinks !== false && !!link.children?.some((child) => child.isActive);

const hasEntryAddress = ({ entry }: { entry: MegaMenuEntry }): boolean =>
  typeof entry.slug === "string" && entry.slug.trim() !== "";

export const resolveMenuItemMode = ({
  link,
  megaMenuData,
}: {
  link: HeaderV2Link;
  megaMenuData?: MegaMenuData;
}): MenuItemMode => {
  const source = resolveMegaMenuSource({ link });
  if (source) {
    const data = megaMenuData?.[source.linkId];
    if (
      data?.status === MegaMenuStatus.LOADING ||
      (data?.status === MegaMenuStatus.READY && data.entries.some((entry) => hasEntryAddress({ entry })))
    ) {
      return MenuItemMode.MEGAMENU;
    }
  }
  return hasActiveChildren({ link }) ? MenuItemMode.SUBMENU : MenuItemMode.LINK;
};

export const clampMegaMenuColumns = ({ value }: { value?: number }) =>
  clamp({
    value: Math.round(Number(value) || MEGA_MENU_COLUMNS.default),
    min: MEGA_MENU_COLUMNS.min,
    max: MEGA_MENU_COLUMNS.max,
  });

const readText = ({ structure, template }: { structure: DynamicStructure; template: string }): string | undefined =>
  interpolate({ structure, template }).text.trim() || undefined;

const resolveEntryId = ({ entry, fallback }: { entry: MegaMenuEntry; fallback: string }): string => {
  const metadataId = (entry._metadata as { id?: unknown } | undefined)?.id;
  if (typeof metadataId === "string" || typeof metadataId === "number") return String(metadataId);
  if (typeof entry.id === "string" || typeof entry.id === "number") return String(entry.id);
  return fallback;
};

export const resolveMegaMenuCards = ({
  entries,
  structure,
  item,
}: {
  entries: MegaMenuEntry[];
  structure?: DynamicStructure;
  item?: MegaMenuItemConfig;
}): MegaMenuCard[] => {
  const fields = item?.fields ?? {};
  const channelPrefix = buildChannelPrefix(structure?.channel?.slug);

  return entries.flatMap((entry) => {
    if (!hasEntryAddress({ entry })) return [];
    const itemSlug = String(entry.slug);
    const path = buildEntryPath({ listingLink: item?.moduleSlug, itemSlug });
    if (!path) return [];

    const entryStructure: DynamicStructure = { ...structure, section: entry };
    const label =
      (fields.title ? readText({ structure: entryStructure, template: fields.title }) : undefined) ??
      resolveEntryTitle({ entry: entry as { slug?: string | null; _metadata?: { title?: unknown } } }) ??
      itemSlug;

    const image =
      fields.showImage === false
        ? undefined
        : fields.image
          ? interpolate({ structure: entryStructure, template: fields.image }).image
          : interpolate({
              structure: entryStructure,
              infer: { type: ContentFieldType.IMAGE, context: "section" },
            }).image;

    return [
      {
        id: resolveEntryId({ entry, fallback: itemSlug }),
        label,
        title: fields.showTitle === false ? undefined : label,
        image: image?.src ? { src: image.src, alt: image.alt } : undefined,
        description:
          fields.showDescription === false || !fields.description
            ? undefined
            : readText({ structure: entryStructure, template: fields.description }),
        tag: fields.showTag === false || !fields.tag ? undefined : readText({ structure: entryStructure, template: fields.tag }),
        url: `${channelPrefix}${path}`,
      },
    ];
  });
};

export const resolveSeeAllUrl = ({ link, structure }: { link: HeaderV2Link; structure?: DynamicStructure }): string => {
  const seeAllRedirect = link.megaMenu?.seeAllRedirect;
  const chosen = seeAllRedirect ? getLinkUrl({ item: { name: link.name, redirect: seeAllRedirect } }) : "#";
  if (chosen !== "#") return chosen;
  const listingPath = buildEntryPath({ listingLink: link.megaMenu?.moduleSlug });
  if (listingPath) return `${buildChannelPrefix(structure?.channel?.slug)}${listingPath}`;
  return getLinkUrl({ item: link });
};

export const splitMobileButtons = ({
  buttons,
}: {
  buttons: HeaderV2Button[];
}): { bar: HeaderV2Button[]; drawer: HeaderV2Button[] } => {
  const bar: HeaderV2Button[] = [];
  const drawer: HeaderV2Button[] = [];
  let defaultBarSlotTaken = false;
  for (const button of buttons) {
    const mode = button.mobileDisplay ?? (defaultBarSlotTaken ? ButtonMobileDisplay.DRAWER : ButtonMobileDisplay.BAR);
    if (!button.mobileDisplay) defaultBarSlotTaken = true;
    if (mode === ButtonMobileDisplay.BAR) bar.push(button);
    else if (mode === ButtonMobileDisplay.DRAWER) drawer.push(button);
  }
  return { bar, drawer };
};
